import {useIntl} from 'react-intl';
import {useEffect, useState} from 'react';
import {useForm, Controller} from 'react-hook-form';

import {usStates} from '../../../../../../../utils/states';
import {DepartmentsHTTP} from '../../../../../providers/departments';
import {SkillsHTTP} from '../../../../../providers/skills';

import Input from '../../../../../../../components/_form/Input';
import InputPassword from '../../../../../../../components/_form/InputPassword';
import MaskedInput from '../../../../../../../components/_form/MaskedInput';
import CurrencyInput from '../../../../../../../components/_form/MaskedInput/Currency';
import Select from '../../../../../../../components/_form/Select';
import Button from '../../../../../../../components/Button';
import Switch from '../../../../../../../components/Switch';
import {FieldsetTitle} from '../../../../../../../components/_form/FieldsetTitle';
import {
  validateWhiteSpaces,
  validateUppercase,
} from '../../../../../../../utils/utils';

const EmployeeForm = ({
  onSubmit,
  initialData = {},
  onCancel = () => {},
  type = 'create',
  loading = false,
  submitErrors = null,
}) => {
  const {
    control,
    register,
    handleSubmit,
    setError,
    clearErrors,
    watch,
    formState: {errors},
  } = useForm({
    defaultValues: {
      is_daily: false,
      ...initialData,
    },
  });
  const {formatMessage} = useIntl();
  const [skills, setSkills] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [seePassword, setSeePassword] = useState(false);
  const isDaily = watch('is_daily', initialData.is_daily || false);

  useEffect(() => {
    async function fetchSkills() {
      const {data} = await SkillsHTTP.get({status: 'active'});
      setSkills(() =>
        data.map((item) => ({
          id: item._id,
          value: item._id,
          label: item.name,
        })),
      );
    }
    fetchSkills();
  }, []);

  useEffect(() => {
    async function fetchDepartments() {
      const {data} = await DepartmentsHTTP.get({
        status: 'active',
        per_page: 120,
      });
      setDepartments(() =>
        data.docs.map((item) => ({
          id: item._id,
          value: item._id,
          label: item.name,
        })),
      );
    }
    fetchDepartments();
  }, []);

  useEffect(() => {
    if (submitErrors && submitErrors.field) {
      setError(submitErrors.field, {
        type: 'manual',
        message: submitErrors.msg || '',
      });
    }
  }, [submitErrors]);

  useEffect(() => clearErrors(), []);

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
        <div className="grid grid-cols-2 gap-4">
          <Input
            label={formatMessage({id: 'name-required'})}
            id="name"
            type="text"
            error={errors.name}
            {...register('name', {required: true})}
          />
        </div>

        <div className="grid grid-cols-3 gap-4">
          <Controller
            rules={{
              required: true,
              pattern: {
                value: /^[^_]*$/i,
                message: 'Provide a valid phone number',
              },
            }}
            name="phone"
            control={control}
            render={({field}) => (
              <MaskedInput
                {...field}
                defaultValue={field.value}
                mask="(999) 999-9999"
                maskPlaceholder="_"
                label={formatMessage({id: 'phone-required'})}
                alwaysShowMask={false}
                removeMask={false}
                error={errors.phone}
                errorMessage={errors.phone ? errors.phone.message : ''}
              />
            )}
          />
          <Input
            label={formatMessage({id: 'email'})}
            id="email"
            type="email"
            error={errors.email}
            {...register('email')}
          />
          <Controller
            name="language"
            control={control}
            render={({field}) => (
              <Select
                {...field}
                label={formatMessage({id: 'language'})}
                id="select-language"
                defaultValue="english"
                options={[
                  {value: 'english', label: formatMessage({id: 'english'})},
                  {
                    value: 'portuguese',
                    label: formatMessage({id: 'portuguese'}),
                  },
                  {value: 'spanish', label: formatMessage({id: 'spanish'})},
                ]}
              />
            )}
          />
        </div>

        <FieldsetTitle>{formatMessage({id: 'adress'})}</FieldsetTitle>

        <div className="grid grid-cols-4 gap-4">
          <Input
            label={formatMessage({id: 'street'})}
            id="street"
            type="text"
            error={errors.street}
            {...register('street')}
          />
          <Input
            label={formatMessage({id: 'city'})}
            id="city"
            type="text"
            error={errors.city}
            {...register('city')}
          />
          <Input
            label={formatMessage({id: 'zip-code'})}
            id="zip_code"
            type="text"
            error={errors.zip_code}
            {...register('zip_code')}
          />
          <Controller
            name="state"
            control={control}
            render={({field}) => (
              <Select
                {...field}
                label={formatMessage({id: 'state'})}
                id="select-state"
                options={usStates}
              />
            )}
          />
        </div>

        <FieldsetTitle>{formatMessage({id: 'account-settings'})}</FieldsetTitle>

        <div className="mb-4">
          <Controller
            name="is_daily"
            control={control}
            render={({field}) => (
              <Switch
                label={formatMessage({id: 'daily-pay-toggle'})}
                checked={Boolean(field.value)}
                onChange={field.onChange}
              />
            )}
          />
        </div>

        <div className="grid grid-cols-3 gap-4">
          <Controller
            name="department_id"
            control={control}
            rules={{required: true}}
            render={({field}) => (
              <Select
                {...field}
                label={formatMessage({id: 'department-required'})}
                id="select-department"
                error={errors.department_id}
                options={departments}
              />
            )}
          />
          <Controller
            name="skill_id"
            control={control}
            rules={{required: true}}
            render={({field}) => (
              <Select
                {...field}
                label={formatMessage({id: 'skill-trade-required'})}
                id="select-skill"
                error={errors.skill_id}
                options={skills}
              />
            )}
          />
          <Controller
            rules={{
              required: true,
            }}
            name="hourprice"
            control={control}
            render={({field}) => (
              <CurrencyInput
                {...field}
                label={formatMessage({
                  id: isDaily ? 'daily-rate-required' : 'hourly-rate-required',
                })}
                error={errors.hourprice}
              />
            )}
          />
        </div>

        <div className="grid grid-cols-3 gap-4">
          <Input
            label={formatMessage({id: 'username-required'})}
            id="username"
            type="text"
            error={errors.username}
            errorMessage={errors.username ? errors.username.message : ''}
            {...register('username', {
              required: true,
              validate: {
                whitespace: (val) =>
                  validateWhiteSpaces(val) || 'Remove all white spaces!',
                uppercaseChar: (val) =>
                  validateUppercase(val) || 'Remove all uppercase letters!',
              },
            })}
          />
          <InputPassword
            label={formatMessage({id: 'password-required'})}
            id="password"
            error={errors.password}
            onShowPassword={() => setSeePassword((prev) => !prev)}
            seePassword={seePassword}
            {...register('password', {
              required: type === 'create',
              min: 3,
              validate: {
                whitespace: (val) =>
                  validateWhiteSpaces(val) || 'Remove all white spaces!',
              },
            })}
          />
        </div>
      </div>

      <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
        <Button type="submit" loading={loading}>
          {formatMessage({id: 'save'})}
        </Button>
        {type === 'create' && (
          <Button
            variant="secondary"
            extraClassName="mr-2"
            onClick={() => onCancel()}>
            {formatMessage({id: 'cancel'})}
          </Button>
        )}
      </div>
    </form>
  );
};

export default EmployeeForm;
